import { ExternalLink, FileText } from 'lucide-react';
import { formatDateTime } from '@/lib/date-display.js';
import OpenItem from './OpenItem.jsx';
import { NOT_RECORDED } from './words.js';
import { openDocument } from '@/lib/openDocument.js';
import { openStoredDocument } from '@/lib/openDocument.js';

// THE DOCUMENTS on the account's terms, newest first as the payload orders
// them: kind, label, which term, when received, a WorkDrive link. Replaces
// nothing: the V2 DocumentsPanel renders a WorkDrive folder tree (years,
// deals, files), a different shape from this flat per-term list, so it is not
// reused here (recorded in docs/v3-overnight-1/C-LOG.md).
// A hash-verified signed application (deals-door-1 round 3, R2d-Q1) arrives as ONE entry: stored_document_id opens the
// Supabase copy, and its WorkDrive link sits beneath as the backup line. The server decides the merge; this only renders it.

export default function DocumentsSection({ documents = [], terms = [], byRef, remedyHref }) {
  const termLabel = (id) => { const t = terms.find((x) => x.id === id); return t ? (t.name || t.id) : null; };
  const itemsFor = (d) => [...byRef.entries()].filter(([k]) => k.startsWith(`document:${d.id}:`)).flatMap(([, v]) => v);
  return (
    <section className="rounded-card border border-border bg-surface" data-documents>
      <header className="flex items-baseline justify-between border-b border-border px-4 py-2.5">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Documents</h2>
        <span className="font-mono text-[10px] text-text-dim">{documents.length}</span>
      </header>
      <div className="px-4">
        {documents.length === 0 ? <p className="py-3 text-xs italic text-text-dim">No documents on this account.</p> : null}
        {documents.map((d) => (
          <div key={d.id} className="border-b border-border py-2 last:border-b-0" data-document={d.id}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm">
              <FileText size={14} strokeWidth={1.5} className="text-text-dim" />
              <span className="font-medium text-text">{d.label || d.kind || NOT_RECORDED}</span>
              <span className="text-xs text-text-muted">{d.kind}</span>
              <span className="text-xs text-text-dim">{termLabel(d.term_id) || 'no term'}</span>
              <span className="font-mono text-xs text-text-dim">{d.received_at ? formatDateTime(d.received_at) : NOT_RECORDED}</span>
              {d.stored_document_id ? <button type="button" onClick={() => openStoredDocument(d.stored_document_id)} className="ml-auto flex min-h-[36px] items-center gap-1 text-xs text-info hover:text-accent">Open <ExternalLink size={11} strokeWidth={1.5} /></button>
                : d.workdrive_permalink ? <button type="button" onClick={() => openDocument(d.workdrive_permalink)} className="ml-auto flex min-h-[36px] items-center gap-1 text-xs text-info hover:text-accent">Open <ExternalLink size={11} strokeWidth={1.5} /></button> : null}
            </div>
            {d.backup_workdrive_permalink ? (
              <div className="flex items-center gap-2 pl-6 text-xs text-text-dim" data-document-backup>
                <span>Stored in TRACON, hash verified. Backup:</span>
                <button type="button" onClick={() => openDocument(d.backup_workdrive_permalink)} className="flex min-h-[36px] items-center gap-1 text-info hover:text-accent">WorkDrive copy <ExternalLink size={11} strokeWidth={1.5} /></button>
              </div>
            ) : null}
            {itemsFor(d).map((it, k) => <OpenItem key={it.code + k} item={it} compact href={remedyHref?.(it)} />)}
          </div>
        ))}
      </div>
    </section>
  );
}
